import { Fragment, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CurrentAltContent, LockedContent } from './components/PriceWaterfallPopover';
import { PickupInvestorRankChip } from './PickupInvestorRankChip';
import {
  formatPickupInvestorDisplayName,
  formatPickupProductDisplayName
} from './pickupInvestorDisplay';
import {
  pickupInvestorPricingGainDollars,
  pickupInvestorPricingStatusClass,
  pickupInvestorPricingStatusLabel
} from './pickupInvestorPricingPresentation';
import type { PickupPricingRun } from './pickupRunHistory';

interface PickupPricingRunsPanelProps {
  runs: PickupPricingRun[];
  verificationBlocked?: boolean;
  loading?: boolean;
  loadingMore?: boolean;
  error?: string | null;
  hasMore?: boolean;
  onShowOlder?: () => void;
  onRetry?: () => void;
}

const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const RUN_TIMESTAMP_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
});

function formatRunTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const parts = RUN_TIMESTAMP_FORMATTER.formatToParts(date);
  const datePart = `${parts.find(({ type }) => type === 'month')?.value || ''} ${parts.find(({ type }) => type === 'day')?.value || ''}`;
  const timePart = [
    parts.find(({ type }) => type === 'hour')?.value,
    parts.find(({ type }) => type === 'minute')?.value
  ].filter(Boolean).join(':');
  const dayPeriod = parts.find(({ type }) => type === 'dayPeriod')?.value || '';
  return `${datePart} · ${timePart} ${dayPeriod}`.trim();
}

function formatPrice(value: number | null): string {
  return value === null ? '—' : value.toFixed(3);
}

function formatPoints(value: number | null): string {
  if (value === null) {
    return '—';
  }
  return `${value > 0 ? '+' : ''}${value.toFixed(3)}`;
}

function formatDollars(value: number | null): string {
  return value === null ? '—' : CURRENCY_FORMATTER.format(value);
}

function movementLabel(run: PickupPricingRun): string {
  if (!run.latest) {
    return 'superseded';
  }
  if (run.priorRunDelta === null) {
    return 'first run';
  }
  if (Math.abs(run.priorRunDelta) < 0.0005) {
    return 'flat vs prior run';
  }
  return `${formatPoints(run.priorRunDelta)} vs prior run`;
}

function winnerLabel(run: PickupPricingRun): string {
  if (run.bestPoints === null || run.bestPoints <= 0 || !run.rows[0]) {
    return 'No investor beat the lock';
  }
  return formatPickupInvestorDisplayName(run.rows[0].investor) || run.rows[0].investor;
}

export function PickupPricingRunsPanel({
  runs,
  verificationBlocked = false,
  loading = false,
  loadingMore = false,
  error = null,
  hasMore = false,
  onShowOlder,
  onRetry
}: PickupPricingRunsPanelProps) {
  const latestRunId = runs[0]?.runId ?? null;
  const [openRunOverride, setOpenRunOverride] = useState<string | null | undefined>(undefined);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const effectiveOpenRunId = openRunOverride === undefined ? latestRunId : openRunOverride;

  useEffect(() => {
    setOpenRunOverride(undefined);
    setExpandedProduct(null);
  }, [latestRunId]);

  const summary = useMemo(() => {
    const latest = runs[0];
    if (!latest) {
      return null;
    }
    const bestInvestor = latest.rows[0]
      ? formatPickupInvestorDisplayName(latest.rows[0].investor) || latest.rows[0].investor
      : null;
    return `${runs.length} ${runs.length === 1 ? 'run' : 'runs'} on this lock · latest ${formatRunTimestamp(latest.analyzedAt)} · ${latest.investorCount} ${latest.investorCount === 1 ? 'investor' : 'investors'} · best ${formatPrice(latest.bestPrice)}${bestInvestor ? ` from ${bestInvestor}` : ''}`;
  }, [runs]);
  const explanation = runs[0]
    ? `Each run is one repricing pass against the ${formatPrice(runs[0].lockedPrice)} lock. Open a run to see the investors it returned; open an investor to see its price path. Only the latest run is answerable — older runs are snapshots.`
    : null;

  if (loading && runs.length === 0) {
    return (
      <div className="flex min-h-40 items-center justify-center gap-2 rounded-lg border bg-muted/20 text-sm text-muted-foreground" role="status">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        Loading pricing runs…
      </div>
    );
  }

  if (runs.length === 0) {
    return (
      <div className="space-y-2.5" data-pickup-pricing-runs-panel>
        <div className="rounded-lg border bg-muted/35 px-3 py-2.5">
          <h3 className="text-xs font-semibold text-foreground">No investor priced this lock in the last run.</h3>
        </div>
        <div className="rounded-lg border border-dashed bg-muted/20 px-4 py-10 text-center text-sm text-muted-foreground">
          {verificationBlocked
            ? 'Resolve the verification conflict, then reprice to get a field of quotes.'
            : error || 'No stored investor pricing is available for this loan.'}
          {error && onRetry && (
            <div className="mt-3">
              <Button type="button" variant="outline" size="xs" onClick={onRetry}>Try again</Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <section className="overflow-hidden rounded-lg border bg-card" data-pickup-pricing-runs-panel>
      <div className="border-b bg-muted/35 px-3 py-2.5">
        <h3 className="text-xs font-semibold text-foreground">{summary}</h3>
        {explanation && (
          <p className="mt-1 text-[10.5px] leading-relaxed text-muted-foreground">
            {explanation}
          </p>
        )}
        {error && <p className="mt-1 text-[10px] text-destructive">Older pricing runs could not be loaded: {error}</p>}
      </div>

      <div
        className="grid grid-cols-[14px_minmax(0,1fr)_58px_58px_66px_66px] gap-2 border-b px-[11px] pb-1.5 pt-2.5 text-[9px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
        data-pickup-run-header
      >
        <span />
        <span>Run</span>
        <span className="text-right">Lock</span>
        <span className="text-right">Best</span>
        <span className="text-right">Pickup pts</span>
        <span className="text-right">Gain $</span>
      </div>

      <div className="flex flex-col gap-1.5 p-1.5">
        {runs.map((run) => {
          const runOpen = effectiveOpenRunId === run.runId;
          const quoteCount = run.rows.length;
          return (
            <article key={run.runId} className="overflow-hidden rounded-lg border bg-card" data-pickup-pricing-run={run.runId}>
              <button
                type="button"
                aria-expanded={runOpen}
                data-pickup-run-toggle={run.runId}
                className={cn(
                  'grid w-full grid-cols-[14px_minmax(0,1fr)_58px_58px_66px_66px] items-center gap-2 px-[11px] py-0.5 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  runOpen && 'bg-muted/40'
                )}
                onClick={() => {
                  setOpenRunOverride(runOpen ? null : run.runId);
                  setExpandedProduct(null);
                }}
              >
                {runOpen
                  ? <ChevronDown className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
                  : <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden="true" />}
                <span className="min-w-0">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className={cn('whitespace-nowrap text-[11.5px] font-semibold leading-none', !run.latest && 'text-muted-foreground')}>
                      {formatRunTimestamp(run.analyzedAt)}
                    </span>
                    {run.latest && (
                      <Badge variant="outline" className="h-3.5 border-success/40 bg-success/10 px-1 text-[8px] leading-none text-success-strong">
                        Latest
                      </Badge>
                    )}
                  </span>
                  <span className="mt-0.5 block truncate text-[10px] leading-none text-muted-foreground">
                    {winnerLabel(run)} · {quoteCount} {quoteCount === 1 ? 'quote' : 'quotes'} · {movementLabel(run)}
                  </span>
                </span>
                <span className="whitespace-nowrap text-right font-mono text-[11.5px] tabular-nums text-muted-foreground">{formatPrice(run.lockedPrice)}</span>
                <span className={cn('whitespace-nowrap text-right font-mono text-[11.5px] font-bold tabular-nums', run.bestPoints !== null && run.bestPoints > 0 ? 'text-foreground' : 'text-muted-foreground')}>
                  {formatPrice(run.bestPrice)}
                </span>
                <span className={cn('whitespace-nowrap text-right font-mono text-[11.5px] font-bold tabular-nums', run.bestPoints !== null && run.bestPoints > 0 ? 'text-success-strong' : 'text-muted-foreground')}>
                  {formatPoints(run.bestPoints)}
                </span>
                <span className={cn('whitespace-nowrap text-right font-mono text-[11.5px] tabular-nums', run.bestGainDollars !== null ? 'text-success-strong' : 'text-muted-foreground')}>
                  {formatDollars(run.bestGainDollars)}
                </span>
              </button>

              {runOpen && (
                <div className="border-t" data-pickup-pricing-run-body={run.runId}>
                  <table className="w-full table-fixed border-collapse text-[11.5px] leading-none">
                    <colgroup>
                      <col className="w-[30px]" />
                      <col />
                      <col className="w-[78px]" />
                      <col className="w-[82px]" />
                      <col className="w-[82px]" />
                      <col className="w-[124px]" />
                    </colgroup>
                    <thead className="bg-muted/40 text-[9px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                      <tr>
                        <th scope="col" className="px-2 py-1.5 text-left">#</th>
                        <th scope="col" className="px-2 py-1.5 text-left">Investor</th>
                        <th scope="col" className="px-2 py-1.5 text-right">Price</th>
                        <th scope="col" className="px-2 py-1.5 text-right">Pickup pts</th>
                        <th scope="col" className="px-2 py-1.5 text-right">Gain $</th>
                        <th scope="col" className="px-2 py-1.5 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {run.rows.map((row, index) => {
                        const key = `${run.runId}:${row.product.productId ?? index}:${row.investor}`;
                        const productOpen = expandedProduct === key;
                        const investor = formatPickupInvestorDisplayName(row.investor) || row.investor;
                        const product = formatPickupProductDisplayName(row.productName, row.investor);
                        const gainDollars = pickupInvestorPricingGainDollars(row);
                        return (
                          <Fragment key={key}>
                            <tr
                              tabIndex={0}
                              aria-expanded={productOpen}
                              data-pickup-run-quote={key}
                              className="cursor-pointer border-t hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                              onClick={() => setExpandedProduct(productOpen ? null : key)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  setExpandedProduct(productOpen ? null : key);
                                }
                              }}
                            >
                              <td className="px-2 py-0.5"><PickupInvestorRankChip row={row} /></td>
                              <td className={cn('min-w-0 px-2 py-0.5', row.beatsLock ? 'font-semibold text-foreground' : 'font-normal text-muted-foreground')}>
                                <div className="truncate" title={investor}>{investor}</div>
                                <div className="mt-0.5 truncate text-[10px] font-normal text-muted-foreground" title={row.productName}>{product}</div>
                              </td>
                              <td className="whitespace-nowrap px-2 py-0.5 text-right font-mono tabular-nums">{formatPrice(row.price)}</td>
                              <td className={cn('whitespace-nowrap px-2 py-0.5 text-right font-mono font-bold tabular-nums', row.beatsLock ? 'text-success-strong' : 'text-muted-foreground')}>
                                {formatPoints(row.deltaPoints)}
                              </td>
                              <td className={cn('whitespace-nowrap px-2 py-0.5 text-right font-mono tabular-nums', gainDollars !== null ? 'text-success-strong' : 'text-muted-foreground')}>
                                {formatDollars(gainDollars)}
                              </td>
                              <td className="px-2 py-0.5 text-right">
                                <Badge variant="outline" className={cn('h-5 px-1.5 text-[9px]', pickupInvestorPricingStatusClass(row))}>
                                  {pickupInvestorPricingStatusLabel(row)}
                                </Badge>
                              </td>
                            </tr>
                            {productOpen && (
                              <tr key={`${key}:waterfall`} data-pickup-run-waterfall={key}>
                                <td colSpan={6} className="border-t bg-muted/40 px-[11px] pb-[11px] pt-2">
                                  <CurrentAltContent
                                    type={row.currentInvestor ? 'current' : 'bestAlt'}
                                    productDetail={row.product}
                                    price={row.price}
                                    comparison={{
                                      lockPrice: run.lockedPrice,
                                      currentPrice: run.currentInvestorPrice,
                                      bestAltPrice: run.bestPrice,
                                      loanAmount: run.loanAmount
                                    }}
                                  />
                                  {row.currentInvestor && run.pricingWaterfall && (
                                    <div className="mt-3 border-t pt-3">
                                      <div className="mb-2 text-[9.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                                        Stored locked buy-side waterfall · not this quote
                                      </div>
                                      <LockedContent
                                        waterfall={run.pricingWaterfall}
                                        price={run.lockedPrice}
                                        lockedInvestorPrice={run.lockedInvestorPrice}
                                      />
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </article>
          );
        })}
      </div>

      {hasMore && onShowOlder && (
        <div className="border-t p-2 text-center">
          <Button type="button" variant="ghost" size="xs" disabled={loadingMore} onClick={onShowOlder}>
            {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
            Show older runs
          </Button>
        </div>
      )}
    </section>
  );
}
